import { Euler, Quaternion, Vector3 } from 'three';
import colliders from '../assets/models/colliders.json';
import type { Point3 } from './scenery';

export type StructureKind = 'station' | 'wreck' | 'panel';
export interface Structure { kind: StructureKind; position: Point3; rotation: Point3; scale: number }

/** Authored objects are ordinary scenery in the same fixed coordinate system. */
export const STRUCTURES: Structure[] = [
  { kind: 'station', position: [-70, 34, 50], rotation: [.1, -2.1, .18], scale: .72 },
  { kind: 'wreck', position: [-135, -12, 117], rotation: [.4, -.8, .6], scale: 1.15 },
  { kind: 'panel', position: [-109, 0, 98], rotation: [.6, .8, -.5], scale: 1 },
  { kind: 'panel', position: [-155, -6, 136], rotation: [-.4, -.7, .9], scale: .65 },
  { kind: 'wreck', position: [-1020, 222, -1430], rotation: [-.3, .7, -1.1], scale: .8 },
  { kind: 'panel', position: [-1004, 232, -1440], rotation: [.2, .9, -.4], scale: .9 },
];

export const STRUCTURE_BOXES = STRUCTURES.flatMap(structure => {
  const rotation = new Quaternion().setFromEuler(new Euler(...structure.rotation));
  return colliders[structure.kind].map(box => {
    const q = rotation.clone().multiply(new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), box.rotation));
    return {
      center: new Vector3(...box.center).multiplyScalar(structure.scale).applyQuaternion(rotation).add(new Vector3(...structure.position)),
      half: new Vector3(...box.size).multiplyScalar(structure.scale / 2).addScalar(.6),
      rotation: q, inverse: q.clone().invert(),
    };
  });
});
// Navigation avoids the entire installation; manual pilots can use its open centre.
export const STRUCTURE_OBSTACLES = STRUCTURES.map(item => ({ position: item.position, radius: (item.kind === 'station' ? 46 : item.kind === 'wreck' ? 12 : 5) * item.scale }));

export function sweepStructures(from: Vector3, proposed: Vector3): Vector3 {
  const result = proposed.clone();
  for (const box of STRUCTURE_BOXES) {
    const start = from.clone().sub(box.center).applyQuaternion(box.inverse);
    const end = result.clone().sub(box.center).applyQuaternion(box.inverse);
    const delta = end.clone().sub(start);
    if (Math.abs(start.x) < box.half.x && Math.abs(start.y) < box.half.y && Math.abs(start.z) < box.half.z) {
      const axes = ['x', 'y', 'z'] as const;
      const axis = axes.reduce((a, b) => box.half[a] - Math.abs(start[a]) < box.half[b] - Math.abs(start[b]) ? a : b);
      start[axis] = (start[axis] < 0 ? -1 : 1) * (box.half[axis] + .02);
      result.copy(start.applyQuaternion(box.rotation).add(box.center));
      continue;
    }
    let enter = 0, exit = 1;
    for (const axis of ['x', 'y', 'z'] as const) {
      if (Math.abs(delta[axis]) < 1e-10) {
        if (Math.abs(start[axis]) > box.half[axis]) { enter = Infinity; break; }
      } else {
        const a = (-box.half[axis] - start[axis]) / delta[axis], b = (box.half[axis] - start[axis]) / delta[axis];
        enter = Math.max(enter, Math.min(a, b)); exit = Math.min(exit, Math.max(a, b));
      }
    }
    if (enter <= exit && enter <= 1 && exit >= 0) result.lerpVectors(from, result, Math.max(0, enter - .002));
  }
  return result;
}

export function structureSpeedLimit(position: Vector3, direction: Vector3): number {
  let limit = Infinity;
  for (const box of STRUCTURE_BOXES) {
    const local = position.clone().sub(box.center).applyQuaternion(box.inverse);
    const closest = local.clone().clamp(box.half.clone().negate(), box.half);
    const outward = local.sub(closest).applyQuaternion(box.rotation), distance = outward.length();
    if (distance < 40 && direction.dot(outward.normalize()) < .35) limit = Math.min(limit, Math.max(6, distance * 1.4));
  }
  return limit;
}
